import { API_SERVER_URL } from 'js/constants/api';

const OAUTH_START_URL = `${API_SERVER_URL}/oauth/start`;
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;
const POPUP_FEATURES = 'top=300,left=250,height=600,width=600';
const POPUP_CLOSED_POLL_MS = 500;

let adminApiOrigin: string;
try {
  adminApiOrigin = new URL(API_SERVER_URL).origin;
} catch {
  adminApiOrigin = API_SERVER_URL;
}

export interface OAuthServerFlowParams {
  /**
   * The signed-in user's Nexla session token — NOT an OAuth provider access token, which is
   * what `access_token` means elsewhere in these flows. Passed in by the caller (via
   * `useNexlaSessionToken` / `withNexlaSessionToken`) so this module holds no Redux
   * dependency and never has to import the app entry point to find the store.
   */
  nexlaSessionToken: string | null;
  vendorId?: number;
  authTemplateId?: number;
  credentialName: string;
  formValues?: Record<string, unknown>;
}

/**
 * Mode A (`auto_create_credential: true`, admin-api builds and persists the credential itself)
 * is deliberately **not used**. A completed OAuth dance is not on its own a decision to create a
 * credential, and Mode A hands back a credential whose payload the client never sees — which
 * makes it impossible to diff against what the legacy path writes today.
 *
 * Every flow therefore runs Mode B: the API returns tokens, the client assembles the
 * credential payload (see `buildOAuthTemplateConfig`) and posts it with `enrich_config: true`.
 */
const AUTO_CREATE_CREDENTIAL = false;

/**
 * Client-side half of admin-api's `build_template_config` (`lib/oauth_flow_service.rb`), which
 * only runs in Mode A. Ported from the React SDK's `oauth/serverFlow.ts` so both clients write
 * the same credential shape.
 *
 * `formValues` must already be the *visible* values: hidden parameters (client id/secret, token
 * URLs) are stripped from the form by `OauthParamFilter`, and the server fills them back in from
 * the auth template when the payload carries `enrich_config: true`.
 *
 * The unprefixed `access_token` is written only for a connector whose spec declares that field —
 * gdrive, gcs, bigquery, sharepoint, google_pubsub. There it is load-bearing in the form: their
 * Authorize button hides on `displayOn: [{ name: 'access_token', value: '' }]` and the field is
 * `required`, so omitting it leaves the button on screen and blocks Save. Everywhere else it is a
 * third copy of a rotating secret that nothing refreshes: box and dropbox gate on
 * `vendor.access_token`, and a templatized connector's synthesized button gates on
 * `oauth2.access_token` — both set above. Templatized connectors never carried it before this
 * flow existed, which is what `authFields` keeps true.
 *
 * admin-api writes the unprefixed key itself in `build_template_config`, but only in Mode A where
 * it creates the credential; this client is Mode B, so nothing puts it back.
 */
export function buildOAuthTemplateConfig(
  formValues: Record<string, unknown> = {},
  tokens: Record<string, unknown> = {},
  authFields: { name?: string }[] = [],
): Record<string, unknown> {
  const config: Record<string, unknown> = { ...formValues };

  for (const [key, value] of Object.entries(tokens)) {
    config[`oauth2.${key}`] = value;
  }

  if (tokens.access_token !== undefined) {
    config['vendor.access_token'] = tokens.access_token;
    config['oauth2.access.token'] = tokens.access_token;
    if (authFields.some((field) => field?.name === 'access_token')) {
      config['access_token'] = tokens.access_token;
    }
  }
  if (tokens.refresh_token !== undefined) config['vendor.refresh_token'] = tokens.refresh_token;
  if (tokens.token_type !== undefined) config['vendor.token_type'] = tokens.token_type;
  if (tokens.expires_in !== undefined) {
    config['vendor.token_expires_in'] = tokens.expires_in;
    config['vendor.has_token_expiration_ts'] = true;
  }

  const now = new Date().toISOString();
  config['vendor.last_refreshed_at'] = now;
  config['oauth2.authorizedAt'] = now;
  config['vendor.auth_scheme'] = 'header';

  return config;
}

const OAUTH_FORM_UI_KEYS = new Set([
  'cred_name',
  'cred_desc',
  'cred_type',
  'template',
  '_vendorConnectionType',
]);

export function filterOAuthFormValues(data: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).filter(
      ([k]) =>
        !OAUTH_FORM_UI_KEYS.has(k) &&
        !k.endsWith('.fetch_token_button') &&
        !k.endsWith('.fetch_token'),
    ),
  );
}

export type OAuthServerFlowResult =
  | { type: 'tokens'; tokens: Record<string, unknown> }
  | { type: 'credential'; id: number; name: string };

interface OAuthStartResponse {
  popup_url: string;
  sid: string;
}

interface OAuthResultMessage {
  type: 'nexla-oauth-result';
  success: boolean;
  sid: string;
  credential?: { id: number; name: string };
  tokens?: Record<string, unknown>;
  error?: string;
}

export function oauthServerFlow(params: OAuthServerFlowParams): Promise<OAuthServerFlowResult> {
  const { nexlaSessionToken, vendorId, authTemplateId, credentialName, formValues } = params;
  if (!nexlaSessionToken) {
    return Promise.reject(new Error('No access token available — user may not be authenticated'));
  }

  // Open blank popup synchronously inside the user gesture to avoid popup blockers.
  // We navigate it to popup_url after /oauth/start responds.
  // Unique name per flow. A fixed name makes `window.open` target an *existing* window of that
  // name — and because COOP also blocks our `popup.close()`, a previous popup can linger on the
  // provider's origin and get reused, so the next flow starts holding a cross-origin handle.
  const popup = window.open('about:blank', `NexlaOAuth_${Date.now()}`, POPUP_FEATURES);

  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let pollIntervalId: ReturnType<typeof setInterval> | null = null;
    let sid: string | null = null;
    let fetchDone = false;

    const cleanup = () => {
      settled = true;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (pollIntervalId !== null) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
      }
      window.removeEventListener('message', handleMessage);
      try {
        popup?.close();
      } catch {
        /* COOP may block this — popup will close on its own */
      }
    };

    // Detect when the popup closes before a postMessage arrives.
    // This covers COOP failures: providers like Box/Salesforce/Asana set COOP on their
    // auth pages, which severs window.opener. Admin-api's callback then silently skips
    // postMessage. The popup sits on "Completing authorization..." until the user closes it.
    // Without this poll, the promise would hang for POPUP_TIMEOUT_MS (5 min).
    //
    // The message deliberately does not name COOP. This same path fires whenever the popup closes
    // with no result, and the overwhelmingly common reason is that the user cancelled on the
    // provider's consent screen. Naming a cause we cannot distinguish turns a cancel into an
    // alarming bug report.
    pollIntervalId = setInterval(() => {
      if (!popup || !popup.closed || settled) return;
      cleanup();
      reject(new Error('Authorization was not completed. Please try again.'));
    }, POPUP_CLOSED_POLL_MS);

    const handleMessage = (event: MessageEvent<OAuthResultMessage>) => {
      if (event.origin !== adminApiOrigin) return;
      const data = event.data;
      if (data?.type !== 'nexla-oauth-result') return;

      // Buffer messages that arrive before /oauth/start resolves (sid not yet known).
      pendingMessages.push(data);
      processPending();
    };

    const pendingMessages: OAuthResultMessage[] = [];

    function processPending() {
      if (!fetchDone || sid === null || settled) return;
      for (const data of pendingMessages) {
        if (data.sid !== sid) continue;
        cleanup();
        if (!data.success) {
          reject(new Error(data.error ?? 'OAuth flow failed'));
        } else if (data.tokens) {
          resolve({ type: 'tokens', tokens: data.tokens });
        } else if (data.credential) {
          resolve({ type: 'credential', id: data.credential.id, name: data.credential.name });
        } else {
          reject(new Error('OAuth flow returned no result'));
        }
        return;
      }
    }

    window.addEventListener('message', handleMessage);

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('OAuth flow timed out'));
    }, POPUP_TIMEOUT_MS);

    fetch(OAUTH_START_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${nexlaSessionToken}`,
      },
      body: JSON.stringify({
        vendor_id: vendorId,
        ...(authTemplateId !== undefined && { auth_template_id: authTemplateId }),
        credential_name: credentialName,
        ...(formValues && Object.keys(formValues).length > 0 && { form_values: formValues }),
        auto_create_credential: AUTO_CREATE_CREDENTIAL,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`POST /oauth/start failed: ${res.status} ${res.statusText}`);
        return res.json() as Promise<OAuthStartResponse>;
      })
      .then(({ popup_url, sid: returnedSid }) => {
        // Deliberately not reading `popup.closed` here. It only covers the ~200ms window where
        // the user closed the popup during this fetch — which the poll and the timeout already
        // handle — and under COOP the read is blocked and logs a console error on every flow.
        if (!popup) throw new Error('OAuth popup was blocked');
        popup.location.href = popup_url;
        sid = returnedSid;
        fetchDone = true;
        processPending();
      })
      .catch((err: Error) => {
        cleanup();
        reject(err);
      });
  });
}
